import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "@ifgengineering/component-library";
import { ThemedText } from "@components/ThemedText";
import { V3Button } from "@components/v3/primitives";
import { fetchIfIsaAccountAPI } from "@state/ifIsa/ifIsa.actions";
import {
  getIfIsaAccountSelector,
  getIfIsaYearlyContributionSelector,
  isIfIsaAccountNeverLoadedSelector,
} from "@state/ifIsa/ifIsa.selectors";
import { IfIsaPageContainer } from "./styled";
import PageLayout from "../../../layouts/PageLayout/PageLayout.component";
import { IfIsaPausedCard } from "../IfIsaPausedCard";
import { IfIsaSummaryCard } from "../IfIsaSummaryCard";
import { IfIsaTransferBannerCard } from "../IfIsaTransferBannerCard";
import { MoveToIsaCard } from "../MoveToIsaCard";
import { investorSelector } from "@state/investor/investor.selectors";
import useAnalytics from "@helpers/useAnalytics";
import { logException } from "@helpers/logException/logException";
import { useCanShowIsaWrapEntryPoint } from "@hooks/useCanShowIsaWrapEntryPoint";
import { IfIsaInvestmentsSection } from "./IfIsaInvestmentsSection";
import {
  neverLoadedCommitmentsSelector,
  portfolioDataSelector,
} from "@state/commitment/commitment.selectors";
import {
  ifIsaLiveDealsSelector,
  ifIsaTransferDestinationsSelector,
  neverLoadedIfIsaLiveDealsSelector,
  neverLoadedIfIsaTransferDestinationsSelector,
} from "@state/deal/deal.selectors";
import { fetchCommitmentsAPI } from "@state/commitment/commitment.actions";
import {
  fetchIfIsaLiveDealsAPI,
  fetchIfIsaTransferDestinationsAPI,
} from "@state/deal/deal.actions";
import { IfIsaDealsSection } from "./IfIsaDealsSection";
import { navigate } from "gatsby";
import styled, { useTheme } from "styled-components";
import InfoIcon from "@icon-park/react/es/icons/Info";
import ModalWindow from "../../ModalWindow";
import { StringParam, useQueryParam } from "use-query-params";
import { useIfIsaAnalytics } from "@hooks/useIfIsaAnalytics";

const ReferrerMap: Record<string, string> = {
  portfolio: "Portfolio",
  navbar: "Side Menu",
};

export const IfIsaPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const ifIsaAccount = useSelector(getIfIsaAccountSelector);
  const yearlyContribution = useSelector(getIfIsaYearlyContributionSelector);
  const ifIsaLiveDeals = useSelector(ifIsaLiveDealsSelector);
  const ifIsaHoldingDeals = useSelector(ifIsaTransferDestinationsSelector);
  const { canWrap: canShowIsaWrapEntryPoint } = useCanShowIsaWrapEntryPoint();
  const investor = useSelector(investorSelector);
  const fireIsaWrapRequestEntryPointClicked = useAnalytics(
    "isaWrapRequestEntryPointClicked"
  );
  const portfolioData = useSelector(portfolioDataSelector);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const neverLoadedIfIsaAccount = useSelector(
    isIfIsaAccountNeverLoadedSelector
  );
  const neverLoadedIfIsaLiveDeals = useSelector(
    neverLoadedIfIsaLiveDealsSelector
  );
  const neverLoadedIfIsaTransferDestinations = useSelector(
    neverLoadedIfIsaTransferDestinationsSelector
  );
  const neverLoadedCommitments = useSelector(neverLoadedCommitmentsSelector);
  const isInitialDataLoading =
    neverLoadedIfIsaAccount ||
    neverLoadedIfIsaLiveDeals ||
    neverLoadedIfIsaTransferDestinations ||
    neverLoadedCommitments;

  const [referrer] = useQueryParam("referrer", StringParam);

  const { triggerIfIsaPermanentPageViewed } = useIfIsaAnalytics();

  useEffect(() => {
    triggerIfIsaPermanentPageViewed({
      source: ReferrerMap[referrer ?? ""],
      deal_name: "",
    });
  }, []);

  const amountInvested = yearlyContribution?.totalContribution || 0;
  const apiAvailableToInvest = yearlyContribution?.availableToInvest;
  const isPaused = ifIsaAccount?.status === "PAUSED";

  useEffect(() => {
    if (!neverLoadedIfIsaAccount && !ifIsaAccount) {
      navigate("/app/deals", { replace: true });
    }
  }, [neverLoadedIfIsaAccount, ifIsaAccount]);

  useEffect(() => {
    if (neverLoadedIfIsaAccount) {
      dispatch(fetchIfIsaAccountAPI());
    }

    if (neverLoadedIfIsaLiveDeals && !neverLoadedIfIsaAccount) {
      dispatch(fetchIfIsaLiveDealsAPI());
    }
    if (neverLoadedIfIsaTransferDestinations && !neverLoadedIfIsaAccount) {
      dispatch(fetchIfIsaTransferDestinationsAPI());
    }
    if (neverLoadedCommitments && !neverLoadedIfIsaAccount) {
      dispatch(fetchCommitmentsAPI());
    }
  }, [
    dispatch,
    neverLoadedIfIsaLiveDeals,
    neverLoadedIfIsaTransferDestinations,
    neverLoadedCommitments,
    neverLoadedIfIsaAccount,
  ]);

  const handleMoveToIsaClick = async () => {
    try {
      const sender = await fireIsaWrapRequestEntryPointClicked;
      await sender({
        is_member: investor?.isMember ?? false,
        user_id: investor?.id ?? "",
        email: investor?.email ?? "",
        isa_id: String(ifIsaAccount?.id ?? ""),
      });
    } catch (error) {
      logException({
        exception: error,
        message: "IfIsaPage: failed to fire isaWrapRequestEntryPointClicked",
      });
    }
    navigate("/app/if-isa/wrap/deal-selection");
  };

  const canShowMoveToIsaCta = canShowIsaWrapEntryPoint && !!ifIsaAccount;

  if (isInitialDataLoading) {
    return <Loader />;
  }

  return (
    <PageLayout>
      <HeaderContainer>
        <ThemedText type="H24" color="SLATE900">
          IF-ISA Details
        </ThemedText>
        <StyledInfoIcon
          size="22px"
          fill={theme.colors.ui.icon.tertiary}
          onClick={() => setShowInfoModal(true)}
        />
        <InfoModal
          show={showInfoModal}
          onClose={() => setShowInfoModal(false)}
        />
      </HeaderContainer>
      <IfIsaPageContainer>
        <IfIsaSummaryCard
          amountInvested={amountInvested}
          availableToInvest={apiAvailableToInvest}
          pendingMovesIn={yearlyContribution?.pendingMovesIn}
          allowanceUsed={yearlyContribution?.allowanceUsed}
        />
        {isPaused && <IfIsaPausedCard />}
        <IsaOptionsSection>
          <ThemedText type="H18" color="SLATE800">
            ISA Options
          </ThemedText>
          {canShowMoveToIsaCta && (
            <MoveToIsaCard onClick={handleMoveToIsaClick} />
          )}
          <IfIsaTransferBannerCard />
        </IsaOptionsSection>
        <IfIsaInvestmentsSection
          deals={ifIsaHoldingDeals}
          commitments={portfolioData}
        />
        <IfIsaDealsSection deals={ifIsaLiveDeals} />
      </IfIsaPageContainer>
    </PageLayout>
  );
};

export default IfIsaPage;

const InfoModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  return (
    <ModalWindow show={show} onClose={onClose}>
      <InfoModalContent>
        <ThemedText type="T24" color="SLATE700">
          IF-ISA Information
        </ThemedText>
        <ThemedText type="P16" color="SLATE700" forwardedAs="div">
          You have an ISA account with Cur8 Capital. The following regulations
          apply:
          <ul>
            <li>You must be a UK resident and taxpayer to open an ISA.</li>
            <li>
              You can open and contribute to multiple ISA accounts across ISA
              managers in the same tax year (so if you have an unfunded IF-ISA
              with another ISA manager you won’t need to transfer it over to
              us).
            </li>
            <li>
              You can fund a maximum of £20,000 across all of your ISAs within a
              tax year (6 April - 5 April).
            </li>
          </ul>
        </ThemedText>
        <V3Button size="lg" testID="if-isa-info-close" onClick={onClose}>
          Close
        </V3Button>
      </InfoModalContent>
    </ModalWindow>
  );
};

const IsaOptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledInfoIcon = styled(InfoIcon)`
  cursor: pointer;
`;

const InfoModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ul {
    margin-top: 16px;
    padding-left: 24px;
  }
  li {
    display: list-item;
    line-height: 1.5;
    list-style-position: outside;
    p {
      display: inline !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;
